import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Check, Baby, Star, Building2, Users, Zap, Shield, 
  HeadphonesIcon, BarChart3, Code, ArrowRight, FileText, ExternalLink, Calculator, Sparkles
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

import API from '@/config/api';
import { DemoVideoButton } from '@/components/DemoVideoButton';
// API imported from config;

const PricingPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isYearly, setIsYearly] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Interactive calculator state
  const [calcChildren, setCalcChildren] = useState(20);
  const [calcStaff, setCalcStaff] = useState(5);

  useEffect(() => {
    fetchPlans();
    if (user) {
      fetchCurrentPlan();
    }
    
    // Handle subscription callbacks
    const subscription = searchParams.get('subscription');
    if (subscription === 'success') {
      toast.success('Subscription activated successfully!');
    } else if (subscription === 'cancelled') {
      toast.info('Subscription was cancelled');
    }
  }, [user, searchParams]);

  const fetchPlans = async () => {
    try {
      setError(null);
      const res = await axios.get(`${API}/subscriptions/plans`);
      setPlans(res.data || []);
    } catch (error) {
      console.error('Failed to load pricing plans:', error);
      setError(error.message || 'Failed to load pricing plans');
      // Set fallback plans so the page isn't blank
      setPlans([
        {
          id: "basic",
          name: "Basic",
          description: "Perfect for small family child care",
          price: 99,
          features: ["Up to 10 children", "2 staff accounts", "All features included", "Email support"],
          limits: { max_children: 10, max_staff: 2 }
        },
        {
          id: "professional",
          name: "Professional", 
          description: "For growing child care centers",
          price: 149,
          popular: true,
          features: ["Up to 49 children", "10 staff accounts", "All features included", "Priority support"],
          limits: { max_children: 49, max_staff: 10 }
        },
        {
          id: "enterprise",
          name: "Enterprise",
          description: "For large facilities & multi-site",
          price: 249,
          features: ["Up to 100 children", "15 staff accounts", "All features included", "24/7 phone support"],
          limits: { max_children: 100, max_staff: 15 }
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentPlan = async () => {
    try {
      const res = await axios.get(`${API}/subscriptions/my-subscription`, getAuthHeaders());
      if (res.data.has_subscription) {
        setCurrentPlan(res.data.subscription);
      }
    } catch (error) {
      console.error('Failed to load current plan');
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user) {
      toast.error('Please log in to subscribe');
      return;
    }

    // Show terms dialog first
    setSelectedPlan(planId);
    setTermsAccepted(false);
    setShowTermsDialog(true);
  };

  const handleConfirmSubscription = async () => {
    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions to continue');
      return;
    }

    setShowTermsDialog(false);
    setSubscribing(true);
    try {
      const res = await axios.post(
        `${API}/subscriptions/checkout`,
        {
          plan_id: selectedPlan,
          origin_url: window.location.origin
        },
        getAuthHeaders()
      );
      
      if (res.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setSubscribing(false);
    }
  };

  const getIcon = (feature) => {
    if (feature.includes('children')) return <Users className="w-4 h-4" />;
    if (feature.includes('staff')) return <Users className="w-4 h-4" />;
    if (feature.includes('AI')) return <Zap className="w-4 h-4" />;
    if (feature.includes('compliance')) return <Shield className="w-4 h-4" />;
    if (feature.includes('support')) return <HeadphonesIcon className="w-4 h-4" />;
    if (feature.includes('analytics')) return <BarChart3 className="w-4 h-4" />;
    if (feature.includes('API')) return <Code className="w-4 h-4" />;
    if (feature.includes('facility')) return <Building2 className="w-4 h-4" />;
    return <Check className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="pricing-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-primary/5 to-background pt-12 pb-24">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
              <Baby className="w-7 h-7 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-2xl">CareKids WI</span>
          </Link>
          
          <h1 className="font-heading font-bold text-4xl md:text-5xl mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Choose the plan that fits your child care facility. All plans include Wisconsin DCF compliance tools.
          </p>
          <div className="mb-8">
            <DemoVideoButton className="rounded-full px-6" label="Watch the demo first" testId="pricing-watch-demo-btn" />
          </div>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4">
            <Label htmlFor="billing" className={!isYearly ? 'font-semibold' : 'text-muted-foreground'}>
              Monthly
            </Label>
            <Switch
              id="billing"
              checked={isYearly}
              onCheckedChange={setIsYearly}
            />
            <Label htmlFor="billing" className={isYearly ? 'font-semibold' : 'text-muted-foreground'}>
              Yearly
              <Badge className="ml-2 bg-green-100 text-green-800">Save 20%</Badge>
            </Label>
          </div>
        </div>
      </div>

      {/* ==================== INTERACTIVE PRICING CALCULATOR ==================== */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <Card className="card-clay border-2 border-primary/20 overflow-hidden">
          <div className="bg-gradient-to-br from-primary/5 via-amber-50 to-white p-6 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
                <Calculator className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl md:text-2xl">Estimate your price</h2>
                <p className="text-sm text-muted-foreground">Slide to match your center — we'll pick the cheapest plan.</p>
              </div>
            </div>

            {(() => {
              // ----- Cost math -----
              const PLAN_DEFS = [
                { id: 'basic', name: 'Basic', price: 99, maxChildren: 10, maxStaff: 2, childOverage: null, staffOverage: 5 },
                { id: 'professional', name: 'Professional', price: 149, maxChildren: 49, maxStaff: 10, childOverage: null, staffOverage: 5 },
                { id: 'enterprise', name: 'Enterprise', price: 249, maxChildren: 100, maxStaff: 15, childOverage: 2, staffOverage: 5 },
              ];
              const yearlyDiscount = isYearly ? 0.8 : 1;

              const costFor = (p) => {
                let extra = 0;
                let breakdown = [];
                const childOver = Math.max(0, calcChildren - p.maxChildren);
                if (childOver > 0) {
                  if (p.childOverage === null) {
                    return { fits: false, total: Infinity, base: p.price, extra: 0, breakdown: [] };
                  }
                  extra += childOver * p.childOverage;
                  breakdown.push(`${childOver} extra child${childOver > 1 ? 'ren' : ''} × $${p.childOverage}`);
                }
                const staffOver = Math.max(0, calcStaff - p.maxStaff);
                if (staffOver > 0) {
                  extra += staffOver * p.staffOverage;
                  breakdown.push(`${staffOver} extra staff × $${p.staffOverage}`);
                }
                const monthlyBase = p.price + extra;
                const displayedMonthly = Math.round(monthlyBase * yearlyDiscount);
                return { fits: true, total: displayedMonthly, base: Math.round(p.price * yearlyDiscount), extra: displayedMonthly - Math.round(p.price * yearlyDiscount), breakdown };
              };

              const options = PLAN_DEFS.map(p => ({ plan: p, cost: costFor(p) }));
              const validOptions = options.filter(o => o.cost.fits);
              const recommended = validOptions.length ? validOptions.reduce((a, b) => a.cost.total <= b.cost.total ? a : b) : null;

              return (
                <>
                  {/* Sliders */}
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <div className="flex items-baseline justify-between mb-2">
                        <Label className="text-sm font-semibold flex items-center gap-2">
                          <Baby className="w-4 h-4 text-primary" />
                          Enrolled children
                        </Label>
                        <span className="text-2xl font-bold text-primary tabular-nums" data-testid="calc-children-value">{calcChildren}</span>
                      </div>
                      <Slider
                        value={[calcChildren]}
                        min={1}
                        max={150}
                        step={1}
                        onValueChange={(v) => setCalcChildren(v[0])}
                        data-testid="calc-children-slider"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>1</span><span>50</span><span>100</span><span>150</span>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-baseline justify-between mb-2">
                        <Label className="text-sm font-semibold flex items-center gap-2">
                          <Users className="w-4 h-4 text-primary" />
                          Staff accounts
                        </Label>
                        <span className="text-2xl font-bold text-primary tabular-nums" data-testid="calc-staff-value">{calcStaff}</span>
                      </div>
                      <Slider
                        value={[calcStaff]}
                        min={1}
                        max={40}
                        step={1}
                        onValueChange={(v) => setCalcStaff(v[0])}
                        data-testid="calc-staff-slider"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>1</span><span>10</span><span>20</span><span>40</span>
                      </div>
                    </div>
                  </div>

                  {/* Result */}
                  {recommended ? (
                    <div className="bg-white/80 backdrop-blur rounded-xl p-5 md:p-6 border border-primary/20" data-testid="calc-result">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-4 h-4 text-primary" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-primary">Recommended for you</span>
                          </div>
                          <h3 className="font-heading font-bold text-2xl">{recommended.plan.name} plan</h3>
                          <p className="text-sm text-muted-foreground">
                            Fits {calcChildren} child{calcChildren !== 1 ? 'ren' : ''} & {calcStaff} staff member{calcStaff !== 1 ? 's' : ''}
                            {isYearly ? ' — billed annually (20% off)' : ''}
                          </p>
                        </div>
                        <div className="text-left md:text-right">
                          <div className="flex items-baseline gap-1 md:justify-end">
                            <span className="text-4xl md:text-5xl font-bold text-primary tabular-nums" data-testid="calc-total">${recommended.cost.total}</span>
                            <span className="text-muted-foreground">/mo</span>
                          </div>
                          {isYearly && (
                            <p className="text-xs text-muted-foreground">
                              ${recommended.cost.total * 12} billed yearly
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Breakdown */}
                      {recommended.cost.breakdown.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-dashed border-primary/20 space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{recommended.plan.name} base</span>
                            <span className="tabular-nums">${recommended.cost.base}/mo</span>
                          </div>
                          {recommended.cost.breakdown.map((line, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="text-muted-foreground">{line}</span>
                              <span className="tabular-nums">+ included above</span>
                            </div>
                          ))}
                          <div className="flex justify-between font-semibold text-sm pt-2 border-t border-primary/10">
                            <span>Your monthly total</span>
                            <span className="text-primary tabular-nums">${recommended.cost.total}</span>
                          </div>
                        </div>
                      )}

                      {/* Comparison chips */}
                      <div className="mt-4 flex flex-wrap gap-2">
                        {options.map(({ plan, cost }) => {
                          const isPicked = plan.id === recommended.plan.id;
                          return (
                            <div
                              key={plan.id}
                              className={`px-3 py-2 rounded-lg text-xs border ${
                                isPicked
                                  ? 'bg-primary/10 border-primary text-primary font-semibold'
                                  : cost.fits
                                  ? 'bg-white border-gray-200 text-muted-foreground'
                                  : 'bg-gray-50 border-gray-200 text-gray-400 line-through'
                              }`}
                              data-testid={`calc-chip-${plan.id}`}
                            >
                              {plan.name}: {cost.fits ? `$${cost.total}/mo` : 'over capacity'}
                            </div>
                          );
                        })}
                      </div>

                      <Link to="/register" className="mt-5 block">
                        <Button className="w-full md:w-auto" size="lg" data-testid="calc-cta">
                          Start 7-day free trial on {recommended.plan.name}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="bg-white/80 rounded-xl p-5 text-center border border-primary/20">
                      <p className="text-sm text-muted-foreground">
                        Your center is larger than our standard tiers.
                        {' '}
                        <a href="mailto:info@carekidswi.com" className="text-primary font-semibold underline">
                          Contact us for a custom quote
                        </a>.
                      </p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </Card>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-6xl mx-auto px-4 -mt-16">
        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
            <p className="text-yellow-800 text-sm">
              Note: Showing cached pricing. Live data temporarily unavailable.
            </p>
          </div>
        )}
        
        {/* No Plans Fallback */}
        {plans.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">Unable to load pricing plans.</p>
            <Button onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        )}
        
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative card-clay ${plan.popular ? 'ring-2 ring-primary shadow-xl' : ''}`}
              data-testid={`pricing-plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground px-4 py-1">
                    <Star className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle className="font-heading text-2xl">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold">
                    ${isYearly ? Math.round(plan.price * 0.8) : plan.price}
                  </span>
                  <span className="text-muted-foreground">/month</span>
                </div>
                {isYearly && (
                  <p className="text-sm text-muted-foreground">
                    ${Math.round(plan.price * 0.8 * 12)} billed annually
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Plan Limits Summary */}
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Children</span>
                    <span className="font-semibold">
                      {plan.limits?.max_children >= 999 ? 'Unlimited' : `Up to ${plan.limits?.max_children}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Staff accounts</span>
                    <span className="font-semibold">
                      {plan.limits?.max_staff >= 999 ? 'Unlimited' : `Up to ${plan.limits?.max_staff}`}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  {plan.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="p-1 bg-primary/10 rounded-full text-primary">
                        {getIcon(feature)}
                      </div>
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
                
                {user ? (
                  currentPlan?.plan_id === plan.id ? (
                    <Button className="w-full" variant="outline" disabled>
                      Current Plan
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full ${plan.popular ? 'btn-primary' : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={subscribing}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {subscribing ? 'Processing...' : 'Start 7-day free trial'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  )
                ) : (
                  <Link to={`/register?subscription=pending&plan=${plan.id}`}>
                    <Button 
                      className={`w-full ${plan.popular ? 'btn-primary' : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                    >
                      Start 7-day free trial
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Current Plan Info */}
        {user && currentPlan && (
          <Card className="card-clay mt-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold text-lg">Your Current Plan</h3>
                  <p className="text-muted-foreground">
                    {currentPlan.plan_name || 'Basic'} - {currentPlan.status === 'active' ? 'Active' : currentPlan.status}
                  </p>
                  {currentPlan.current_period_end && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {currentPlan.status === 'canceled' ? 'Access until' : 'Renews'}: {new Date(currentPlan.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">${currentPlan.amount}/mo</p>
                  {currentPlan.status === 'active' && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2 text-red-600 hover:text-red-700"
                      onClick={async () => {
                        if (confirm('Are you sure you want to cancel your subscription?')) {
                          try {
                            await axios.post(`${API}/subscriptions/cancel`, {}, getAuthHeaders());
                            toast.success('Subscription canceled. You will have access until the end of your billing period.');
                            fetchCurrentPlan();
                          } catch (error) {
                            toast.error('Failed to cancel subscription');
                          }
                        }
                      }}
                    >
                      Cancel Subscription
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* FAQ or Additional Info */}
        <div className="mt-16 mb-12 text-center">
          <h2 className="font-heading font-bold text-2xl mb-4">All Plans Include</h2>
          <div className="grid md:grid-cols-4 gap-6 mt-8">
            {[
              { icon: Shield, title: 'DCF Compliance', desc: 'Wisconsin regulations built-in' },
              { icon: Users, title: 'Parent Portal', desc: 'Family communication tools' },
              { icon: Check, title: 'Attendance Tracking', desc: 'Daily check-in/check-out' },
              { icon: HeadphonesIcon, title: 'Support', desc: 'Help when you need it' }
            ].map((item, idx) => (
              <div key={idx} className="p-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
          
          {/* Additional Pricing Info */}
          <div className="mt-8 p-6 bg-muted/30 rounded-xl max-w-2xl mx-auto">
            <h3 className="font-semibold mb-3">Flexible Scaling</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Need more capacity? All plans support additional staff accounts:
            </p>
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex justify-center">
                <span className="font-semibold text-primary">$5/month</span>
                <span className="text-muted-foreground ml-1">per additional staff account (all plans)</span>
              </div>
              <div className="flex justify-center">
                <span className="font-semibold text-primary">$2/month</span>
                <span className="text-muted-foreground ml-1">per additional child (Enterprise only)</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center py-12 border-t">
          <h2 className="font-heading font-bold text-2xl mb-4">Ready to get started?</h2>
          <p className="text-muted-foreground mb-6">
            Choose a plan above to begin. Cancel anytime.
          </p>
          <div className="flex gap-4 justify-center">
            <Button 
              size="lg" 
              className="btn-primary"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              View Plans
            </Button>
            <DemoVideoButton label="Watch Demo" testId="pricing-cta-watch-demo-btn" className="h-11 px-8" />
            <Link to="/login">
              <Button size="lg" variant="outline">
                Sign In
              </Button>
            </Link>
          </div>
        </div>

        {/* Terms Link */}
        <div className="text-center pb-8 text-sm text-muted-foreground">
          By using CareKids WI, you agree to our{' '}
          <Link to="/terms" className="text-primary hover:underline">
            Terms and Conditions
          </Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </div>
      </div>

      {/* Terms and Conditions Dialog */}
      <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Terms and Conditions
            </DialogTitle>
            <DialogDescription>
              Please review and accept our Terms and Conditions before subscribing.
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4 text-sm">
              <p>
                Welcome to <strong>CareKids WI</strong>. By subscribing to our services, you agree to the following terms:
              </p>
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">1. Eligibility</h4>
                <p className="text-muted-foreground">
                  CareKids WI is intended for licensed child care providers, centers, educators, administrators, and parents/legal guardians of enrolled children. You must be at least 18 years old.
                </p>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">2. Platform Purpose</h4>
                <p className="text-muted-foreground">
                  CareKids WI provides digital tools for child attendance, activity tracking, communication, enrollment, billing, and reporting. CareKids WI does not provide child care services directly.
                </p>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">3. Payments and Fees</h4>
                <p className="text-muted-foreground">
                  Fees are billed according to your selected plan and are due as stated at the time of purchase. Fees are non-refundable unless required by law. We reserve the right to modify pricing with reasonable advance notice.
                </p>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">4. Child Data and Privacy</h4>
                <p className="text-muted-foreground">
                  Protecting children&apos;s data is a priority. Personal information is collected and used in accordance with our Privacy Policy. CareKids WI does not sell children&apos;s personal data.
                </p>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">5. Service Availability</h4>
                <p className="text-muted-foreground">
                  We strive to provide reliable access but do not guarantee uninterrupted service. We may perform maintenance, modify features, or temporarily suspend access for security reasons.
                </p>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">6. Termination</h4>
                <p className="text-muted-foreground">
                  You may stop using CareKids WI at any time. We may suspend or terminate your account if you violate these Terms, payments are not made when due, or as required by law.
                </p>
              </div>
              
              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">7. Governing Law</h4>
                <p className="text-muted-foreground">
                  These Terms are governed by the laws of the State of Wisconsin.
                </p>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-blue-800">
                  <strong>Full Documents:</strong> For the complete{' '}
                  <Link to="/terms" target="_blank" className="text-primary hover:underline inline-flex items-center gap-1">
                    Terms and Conditions <ExternalLink className="w-3 h-3" />
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" target="_blank" className="text-primary hover:underline inline-flex items-center gap-1">
                    Privacy Policy <ExternalLink className="w-3 h-3" />
                  </Link>
                </p>
              </div>
            </div>
          </ScrollArea>
          
          <div className="border-t pt-4">
            <div className="flex items-start gap-3 mb-4">
              <Checkbox 
                id="terms" 
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                data-testid="accept-terms-checkbox"
              />
              <Label 
                htmlFor="terms" 
                className="text-sm cursor-pointer leading-relaxed"
              >
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" className="text-primary hover:underline">
                  Terms and Conditions
                </Link>
                {' '}and{' '}
                <Link to="/terms" target="_blank" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
              </Label>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowTermsDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmSubscription}
              disabled={!termsAccepted || subscribing}
              data-testid="confirm-subscription-btn"
            >
              {subscribing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Subscription
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingPage;
